import { $authHost, getAuthIdentity } from "../../http/index";

import {
    getPushDeviceCredential,
    savePushDeviceCredential,
    savePushRuntimeConfig,
    getPushRuntimeConfig
} from "./pushDeviceDb";


const apiBase =
    (
        process.env.REACT_APP_API_URL ||
        ""
    )
        .replace(
            /\/+$/,
            ""
        );


const urlBase64ToUint8Array =
    (
        base64String
    ) => {
        const padding =
            "=".repeat(
                (
                    4 -
                    (
                        base64String.length %
                        4
                    )
                ) %
                4
            );


        const base64 =
            (
                base64String +
                padding
            )
                .replace(
                    /-/g,
                    "+"
                )
                .replace(
                    /_/g,
                    "/"
                );


        const raw =
            window.atob(
                base64
            );


        return Uint8Array.from(
            [
                ...raw
            ].map(
                (
                    character
                ) =>
                    character.charCodeAt(
                        0
                    )
            )
        );
    };


const isIos =
    () =>
        /iPad|iPhone|iPod/.test(
            window.navigator
                .userAgent
        );


const isStandalone =
    () =>
        window.matchMedia?.(
            "(display-mode: standalone)"
        )?.matches ||
        window.navigator
            .standalone ===
        true;


const getToken =
    () =>
        localStorage.getItem(
            "token"
        );


const getPermission =
    () => {
        if (
            !(
                "Notification" in
                window
            )
        ) {
            return "unsupported";
        }


        return (
            Notification.permission ||
            "unknown"
        );
    };


const getClientInfo =
    () => {
        let timezone =
            null;


        try {
            timezone =
                Intl
                    .DateTimeFormat()
                    .resolvedOptions()
                    .timeZone ||
                null;
        } catch (
            error
            ) {
            // optional
        }


        return {
            language:
                navigator.language ||
                null,

            platform:
                navigator.platform ||
                null,

            timezone,

            standalone:
                isStandalone(),

            screen: {
                width:
                    window.screen?.width ||
                    null,

                height:
                    window.screen?.height ||
                    null,

                pixelRatio:
                    window.devicePixelRatio ||
                    1
            }
        };
    };


const getErrorReason =
    (
        error
    ) => {
        const name =
            error?.name ||
            "error";


        const message =
            error?.message ||
            "";


        if (
            name ===
            "NotAllowedError"
        ) {
            return "Браузер не дал разрешение на уведомления";
        }


        if (
            name ===
            "NotSupportedError"
        ) {
            return "Push не поддерживается в этом браузере";
        }


        if (
            name ===
            "InvalidStateError"
        ) {
            return "Service Worker находится в некорректном состоянии";
        }


        if (
            name ===
            "AbortError"
        ) {
            return "Создание push-подписки было прервано браузером";
        }


        return (
            message ||
            name ||
            "Неизвестная ошибка push"
        );
    };


const readResponseBody =
    async (
        response
    ) => {
        const contentType =
            response.headers
                .get(
                    "content-type"
                ) ||
            "";


        if (
            contentType.includes(
                "application/json"
            )
        ) {
            return response
                .json()
                .catch(
                    () => null
                );
        }


        const text =
            await response
                .text()
                .catch(
                    () => ""
                );


        return text
            ? {
                message:
                text
            }
            : null;
    };


const apiRequest = async (path, { method = "GET", body, auth = false, authIdentity } = {}) => {
    if (auth) {
        try {
            // Same refresh single-flight/retry as the rest of the application.
            const response = await $authHost.request({
                url: `${apiBase}${path}`, method, data: body,
                _arentoAuthIdentity: authIdentity === undefined ? getAuthIdentity() : authIdentity
            });
            return response.data || {};
        } catch (cause) {
            const data = cause.response?.data;
            const error = new Error(data?.message || data?.error ||
                (typeof data === "string" && data) || cause.message || "Push request failed");
            error.status = cause.response?.status;
            error.data = data;
            error.code = cause.code;
            throw error;
        }
    }

    // Device credential operations remain independent of JWT and logout.
    const response = await fetch(`${apiBase}${path}`, {
        method, credentials: "include",
        headers: body === undefined ? {} : { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body)
    });
    const data = await readResponseBody(response);
    if (!response.ok) {
        const error = new Error(data?.message || data?.error || `HTTP ${response.status}`);
        error.status = response.status;
        error.data = data;
        throw error;
    }
    return data || {};
};

const serializeSubscription =
    (
        subscription
    ) => {
        if (!subscription) {
            return null;
        }


        return (
            typeof subscription
                .toJSON ===
            "function"
                ? subscription.toJSON()
                : subscription
        );
    };


async function getOrRegisterServiceWorker() {
    if (
        !(
            "serviceWorker" in
            navigator
        )
    ) {
        throw new Error(
            "Service Worker не поддерживается"
        );
    }


    let registration =
        await navigator
            .serviceWorker
            .getRegistration();


    if (
        !registration
    ) {
        registration =
            await navigator
                .serviceWorker
                .register(
                    "/sw.js"
                );
    }


    await navigator
        .serviceWorker
        .ready;


    registration =
        await navigator
            .serviceWorker
            .getRegistration();


    if (
        !registration
    ) {
        throw new Error(
            "Service Worker registration not found"
        );
    }


    return registration;
}


async function getPublicKey() {
    const data =
        await apiRequest(
            "/api/push/public-key"
        );


    if (
        !data?.publicKey
    ) {
        throw new Error(
            "Сервер не вернул VAPID public key"
        );
    }


    /*
     * Сохраняем для будущего Service Worker:
     * pushsubscriptionchange сможет восстановить
     * subscription даже без открытого React.
     */
    await savePushRuntimeConfig({
        apiBase,
        vapidPublicKey:
        data.publicKey
    });


    return data.publicKey;
}


async function createBrowserSubscription(
    registration
) {
    const publicKey =
        await getPublicKey();


    return registration
        .pushManager
        .subscribe({
            userVisibleOnly:
                true,

            applicationServerKey:
                urlBase64ToUint8Array(
                    publicKey
                )
        });
}


async function replaceBrowserSubscription(registration, subscription) {
    if (subscription) {
        await subscription.unsubscribe();
        const current = await registration.pushManager.getSubscription();
        if (current?.endpoint === subscription.endpoint) {
            throw new Error("Браузер не смог обновить push-подписку. Закройте и снова откройте Arento.");
        }
        if (current) return current;
    }
    return createBrowserSubscription(registration);
}


async function getBrowserSubscription({
                                          registerServiceWorker = true
                                      } = {}) {
    let registration;


    if (
        registerServiceWorker
    ) {
        registration =
            await getOrRegisterServiceWorker();
    } else {
        registration =
            await navigator
                .serviceWorker
                .getRegistration();


        if (
            !registration
        ) {
            return {
                registration:
                    null,

                subscription:
                    null
            };
        }
    }


    const subscription =
        await registration
            .pushManager
            .getSubscription();


    return {
        registration,
        subscription
    };
}


const storeClaimResult =
    async (
        result,
        existingCredential
    ) => {
        if (
            !result?.deviceId
        ) {
            throw new Error(
                "Backend не вернул deviceId"
            );
        }


        const deviceSecret =
            result.deviceSecret ||
            existingCredential
                ?.deviceSecret;


        if (
            !deviceSecret
        ) {
            throw new Error(
                "Backend не вернул действующий deviceSecret"
            );
        }


        const credential = {
            deviceId:
            result.deviceId,

            deviceSecret,

            ownershipVersion:
                Number(
                    result.ownershipVersion ||
                    existingCredential
                        ?.ownershipVersion ||
                    1
                )
        };


        await savePushDeviceCredential(
            credential
        );


        return credential;
    };


async function claimDevice(
    subscription,
    {
        enableGlobally = false
    } = {}
) {
    const authIdentity = getAuthIdentity();
    const token =
        getToken();


    if (!token) {
        return {
            ok:
                false,

            skipped:
                true,

            reason:
                "not-authenticated"
        };
    }


    if (
        !subscription
    ) {
        return {
            ok:
                false,

            skipped:
                true,

            reason:
                "no-subscription"
        };
    }


    const existingCredential =
        await getPushDeviceCredential();


    const result =
        await apiRequest(
            "/api/push/device/claim",
            {
                method:
                    "POST",

                auth:
                    true,

                authIdentity,

                body: {
                    deviceId:
                        existingCredential
                            ?.deviceId ||
                        null,

                    deviceSecret:
                        existingCredential
                            ?.deviceSecret ||
                        null,

                    subscription:
                        serializeSubscription(
                            subscription
                        ),

                    permission:
                        getPermission(),

                    clientInfo:
                        getClientInfo(),

                    enableGlobally:
                        enableGlobally ===
                        true
                }
            }
        );


    await storeClaimResult(
        result,
        existingCredential
    );


    return {
        ok:
            true,

        ...result
    };
}


async function reconcileDevice(
    subscription
) {
    const credential =
        await getPushDeviceCredential();


    if (
        !credential
    ) {
        return {
            ok:
                true,

            skipped:
                true,

            reason:
                "no-device-credential"
        };
    }


    try {
        const result =
            await apiRequest(
                "/api/push/device/reconcile",
                {
                    method:
                        "POST",

                    body: {
                        deviceId:
                        credential.deviceId,

                        deviceSecret:
                        credential.deviceSecret,

                        subscription:
                            serializeSubscription(
                                subscription
                            ),

                        permission:
                            getPermission(),

                        clientInfo:
                            getClientInfo()
                    }
                }
            );

        await savePushDeviceCredential({
            deviceId:
            credential.deviceId,

            deviceSecret:
            credential.deviceSecret,

            ownershipVersion:
                Number(
                    result.ownershipVersion ||
                    credential.ownershipVersion ||
                    1
                )
        });

        return {
            ok:
                true,

            ...result
        };

    } catch (
        error
        ) {
        /*
         * Если credential был отозван,
         * но сейчас пользователь авторизован,
         * authenticated claim восстановит identity.
         */
        if (
            (
                error?.status ===
                401 ||
                error?.status ===
                403
            ) &&
            getToken() &&
            subscription
        ) {
            return claimDevice(
                subscription,
                {
                    enableGlobally:
                        false
                }
            );
        }


        throw error;
    }
}


export const getPushPreference =
    async () => {
        try {
            if (
                !getToken()
            ) {
                return {
                    ok:
                        false,

                    pushEnabled:
                        false,

                    reason:
                        "not-authenticated"
                };
            }


            const result =
                await apiRequest(
                    "/api/push/device/preference",
                    {
                        auth:
                            true
                    }
                );


            return {
                ok:
                    true,

                pushEnabled:
                    result
                        ?.pushEnabled ===
                    true,

                version:
                    result?.version ||
                    1
            };

        } catch (
            error
            ) {
            console.error(
                "getPushPreference error:",
                error
            );


            return {
                ok:
                    false,

                pushEnabled:
                    false,

                reason:
                    getErrorReason(
                        error
                    )
            };
        }
    };


export const getPushDiagnostics =
    async () => {
        const result = {
            isSecureContext:
            window.isSecureContext,

            hasServiceWorker:
                "serviceWorker" in
                navigator,

            hasNotification:
                "Notification" in
                window,

            hasPushManager:
                "PushManager" in
                window,

            permission:
                getPermission(),

            isIos:
                isIos(),

            isStandalone:
                isStandalone(),

            serviceWorkerRegistered:
                false,

            serviceWorkerReady:
                false,

            hasBrowserSubscription:
                false,

            hasDeviceCredential:
                false,

            reason:
                "ok",

            error:
                null
        };


        if (
            !result.isSecureContext
        ) {
            result.reason =
                "insecure";

            return result;
        }


        if (
            !result.hasServiceWorker
        ) {
            result.reason =
                "no-sw";

            return result;
        }


        if (
            !result.hasNotification
        ) {
            result.reason =
                "no-notification";

            return result;
        }


        if (
            result.isIos &&
            !result.isStandalone
        ) {
            result.reason =
                "ios-not-standalone";

            return result;
        }


        if (
            !result.hasPushManager
        ) {
            result.reason =
                "no-push-manager";

            return result;
        }


        try {
            const registration =
                await navigator
                    .serviceWorker
                    .getRegistration();


            result.serviceWorkerRegistered =
                Boolean(
                    registration
                );


            if (
                registration
            ) {
                await navigator
                    .serviceWorker
                    .ready;


                result.serviceWorkerReady =
                    true;


                const subscription =
                    await registration
                        .pushManager
                        .getSubscription();


                result.hasBrowserSubscription =
                    Boolean(
                        subscription
                    );
            }


            result.hasDeviceCredential =
                Boolean(
                    await getPushDeviceCredential()
                );

        } catch (
            error
            ) {
            result.reason =
                "diagnostics-error";

            result.error =
                error?.message ||
                String(error);


            return result;
        }


        if (
            result.permission ===
            "denied"
        ) {
            result.reason =
                "denied";
        } else if (
            result.permission ===
            "default"
        ) {
            result.reason =
                "default";
        }


        return result;
    };


/*
 * Главная функция жизненного цикла.
 *
 * Вызывать можно сколько угодно:
 * - startup;
 * - login;
 * - logout;
 * - focus;
 * - visibilitychange.
 */
export const syncPushDevice =
    async () => {
        try {
            await savePushRuntimeConfig({
                apiBase
            });

            /*
             * SW должен знать VAPID public key даже если
             * браузерная subscription пока совершенно исправна.
             *
             * Тогда при pushsubscriptionchange React
             * вообще не понадобится.
             */
            try {
                const runtimeConfig =
                    await getPushRuntimeConfig();


                if (
                    !runtimeConfig?.vapidPublicKey
                ) {
                    await getPublicKey();
                }

            } catch (
                error
                ) {
                /*
                 * Отсутствие сети здесь не должно ломать
                 * обычный reconciliation.
                 */
                console.warn(
                    "VAPID runtime config seed failed:",
                    error
                );
            }

            if (
                !window.isSecureContext ||
                !(
                    "serviceWorker" in
                    navigator
                ) ||
                !(
                    "Notification" in
                    window
                ) ||
                !(
                    "PushManager" in
                    window
                )
            ) {
                return {
                    ok:
                        true,

                    skipped:
                        true,

                    reason:
                        "unsupported"
                };
            }


            if (
                isIos() &&
                !isStandalone()
            ) {
                return {
                    ok:
                        true,

                    skipped:
                        true,

                    reason:
                        "ios-not-standalone"
                };
            }


            const {
                registration,
                subscription:
                    currentSubscription
            } =
                await getBrowserSubscription();


            let subscription =
                currentSubscription;


            const token =
                getToken();


            /*
             * =====================================================
             * AUTHENTICATED
             * =====================================================
             *
             * Существующий browser endpoint ВСЕГДА claim-им.
             *
             * Это принципиально для смены аккаунта на одном
             * устройстве.
             */
            if (
                token &&
                subscription
            ) {
                return await claimDevice(
                    subscription,
                    {
                        enableGlobally:
                            false
                    }
                );
            }


            /*
             * Авторизованы, subscription исчезла,
             * но permission по-прежнему granted.
             *
             * Проверяем глобальное желание пользователя.
             */
            if (
                token &&
                !subscription &&
                Notification.permission ===
                "granted"
            ) {
                const preference =
                    await getPushPreference();


                if (
                    preference.ok &&
                    preference.pushEnabled
                ) {
                    subscription =
                        await createBrowserSubscription(
                            registration
                        );


                    return await claimDevice(
                        subscription,
                        {
                            enableGlobally:
                                false
                        }
                    );
                }
            }


            /*
             * =====================================================
             * WITHOUT JWT
             * =====================================================
             *
             * После logout устройство продолжает жить
             * через собственный credential.
             */
            return await reconcileDevice(
                subscription
            );

        } catch (
            error
            ) {
            console.error(
                "syncPushDevice error:",
                error
            );


            return {
                ok:
                    false,

                reason:
                    getErrorReason(
                        error
                    )
            };
        }
    };


/*
 * Обратная совместимость с тем импортом,
 * который мы добавляли раньше.
 */
export const syncPushSubscription =
    syncPushDevice;


export const isPushSubscribed =
    async () => {
        try {
            if (
                !window.isSecureContext ||
                !(
                    "serviceWorker" in
                    navigator
                ) ||
                !(
                    "Notification" in
                    window
                ) ||
                Notification.permission !==
                "granted"
            ) {
                return false;
            }


            const registration =
                await navigator
                    .serviceWorker
                    .getRegistration();


            if (
                !registration
            ) {
                return false;
            }


            const subscription =
                await registration
                    .pushManager
                    .getSubscription();


            if (
                !subscription
            ) {
                return false;
            }


            /*
             * На странице профиля пользователь авторизован,
             * поэтому учитываем и глобальную настройку.
             */
            if (
                getToken()
            ) {
                const preference =
                    await getPushPreference();


                if (
                    !preference.ok ||
                    !preference.pushEnabled
                ) {
                    return false;
                }
            }


            return true;

        } catch (
            error
            ) {
            console.error(
                "isPushSubscribed error:",
                error
            );


            return false;
        }
    };


export const enablePush =
    async ({ enableGlobally = true, renewSubscription = false } = {}) => {
        const authIdentity = getAuthIdentity();
        const assertCurrentAccount = () => {
            if (getAuthIdentity() !== authIdentity) throw new Error("Аккаунт изменился. Повторите подключение уведомлений.");
        };
        try {
            if (
                !getToken()
            ) {
                return {
                    ok:
                        false,

                    reason:
                        "Нужно войти в аккаунт"
                };
            }


            const diagnostics =
                await getPushDiagnostics();


            if (
                diagnostics.reason ===
                "insecure"
            ) {
                return {
                    ok: false,
                    reason: "Нужен HTTPS"
                };
            }


            if (
                diagnostics.reason ===
                "no-sw"
            ) {
                return {
                    ok: false,
                    reason: "Service Worker не поддерживается"
                };
            }


            if (
                diagnostics.reason ===
                "no-notification"
            ) {
                return {
                    ok: false,
                    reason: "Браузер не поддерживает уведомления"
                };
            }


            if (
                diagnostics.reason ===
                "ios-not-standalone"
            ) {
                return {
                    ok:
                        false,

                    reason:
                        "На iPhone/iPad сначала добавьте Arento на экран «Домой»"
                };
            }


            if (
                diagnostics.reason ===
                "no-push-manager"
            ) {
                return {
                    ok: false,
                    reason: "Push API не поддерживается"
                };
            }


            if (
                Notification.permission ===
                "denied"
            ) {
                return {
                    ok:
                        false,

                    reason:
                        "Уведомления запрещены в настройках браузера"
                };
            }


            if (
                Notification.permission !==
                "granted"
            ) {
                if (!enableGlobally) {
                    return { ok: false, reason: "Включите уведомления на этом устройстве." };
                }

                const permission =
                    await Notification
                        .requestPermission();


                if (
                    permission !==
                    "granted"
                ) {
                    return {
                        ok:
                            false,

                        reason:
                            `Разрешение: ${permission}`
                    };
                }
            }


            const registration =
                await getOrRegisterServiceWorker();


            let subscription =
                await registration
                    .pushManager
                    .getSubscription();


            if (!enableGlobally) {
                const current = await getCurrentPushVerificationStatus();
                const preference = await getPushPreference();
                if (!preference.ok || !preference.pushEnabled || current?.disabledReason === "globally_disabled") {
                    return { ok: false, reason: "Уведомления отключены. Включите их на этом устройстве." };
                }
            }

            assertCurrentAccount();
            let renewed = renewSubscription || Boolean(subscription?.expirationTime && subscription.expirationTime <= Date.now());
            if (renewed) {
                subscription = await replaceBrowserSubscription(registration, subscription);
            } else if (!subscription) {
                subscription = await createBrowserSubscription(registration);
            }


            /*
             * Только явное нажатие пользователя
             * имеет право глобально включить push.
             */
            assertCurrentAccount();
            let result = await claimDevice(subscription, { enableGlobally });

            // A 404/410 rejected endpoint is not fixed by changing its DB status.
            if (result.subscriptionRenewalRequired && !renewed) {
                subscription = await replaceBrowserSubscription(registration, subscription);
                renewed = true;
                assertCurrentAccount();
                result = await claimDevice(subscription, { enableGlobally: false });
            }

            if (result.status !== "active") {
                return { ...result, ok: false, reason: "Уведомления на этом устройстве не включены." };
            }
            return { ...result, subscriptionRenewed: renewed };

        } catch (
            error
            ) {
            console.error(
                "enablePush error:",
                error
            );


            return {
                ok:
                    false,

                reason:
                    getErrorReason(
                        error
                    )
            };
        }
    };


export const disablePush =
    async () => {
        const authIdentity = getAuthIdentity();
        try {
            if (
                !getToken()
            ) {
                return {
                    ok:
                        false,

                    reason:
                        "Нужно войти в аккаунт"
                };
            }


            /*
             * Сначала отключаем ВСЕ устройства аккаунта
             * на backend.
             *
             * Если этот запрос не прошёл, не врём
             * пользователю, будто push глобально выключен.
             */
            await apiRequest(
                "/api/push/device/disable",
                {
                    method:
                        "POST",

                    auth:
                        true,

                    authIdentity,

                    body:
                        {}
                }
            );


            /*
             * Потом физически удаляем browser
             * subscription текущего устройства.
             *
             * Даже если браузер здесь ошибётся,
             * backend уже гарантированно ничего
             * этому пользователю не отправит.
             */
            try {
                const registration =
                    await navigator
                        .serviceWorker
                        .getRegistration();


                const subscription =
                    await registration
                        ?.pushManager
                        ?.getSubscription();


                if (
                    subscription && getAuthIdentity() === authIdentity
                ) {
                    await subscription
                        .unsubscribe();
                }

            } catch (
                browserError
                ) {
                console.warn(
                    "Browser push unsubscribe error:",
                    browserError
                );
            }


            /*
             * deviceId + secret специально НЕ удаляем.
             *
             * Device identity остаётся стабильной.
             * Если пользователь потом снова включит push,
             * authenticated claim оживит устройство.
             */


            return {
                ok:
                    true
            };

        } catch (
            error
            ) {
            console.error(
                "disablePush error:",
                error
            );


            return {
                ok:
                    false,

                reason:
                    getErrorReason(
                        error
                    )
            };
        }
    };

/*
 * Закрывает только уведомления с указанными tag.
 *
 * Никакой глобальной очистки и никакого clearAppBadge:
 * другие чаты / заявки / события пользователя
 * трогать нельзя.
 */
const clearPushNotificationsByTags =
    async (
        tags
    ) => {
        try {
            const normalizedTags =
                new Set(
                    (
                        Array.isArray(tags)
                            ? tags
                            : []
                    )
                        .map(
                            tag =>
                                String(
                                    tag ||
                                    ""
                                ).trim()
                        )
                        .filter(Boolean)
                );


            if (
                normalizedTags.size ===
                0
            ) {
                return {
                    ok:
                        true,

                    closed:
                        0
                };
            }


            if (
                !(
                    "serviceWorker" in
                    navigator
                )
            ) {
                return {
                    ok:
                        true,

                    closed:
                        0
                };
            }


            const registration =
                await navigator
                    .serviceWorker
                    .getRegistration();


            if (
                !registration ||
                !(
                    "getNotifications" in
                    registration
                )
            ) {
                return {
                    ok:
                        true,

                    closed:
                        0
                };
            }


            const notifications =
                await registration
                    .getNotifications();


            let closed =
                0;


            notifications.forEach(
                notification => {
                    const tag =
                        String(
                            notification?.tag ||
                            ""
                        );


                    if (
                        normalizedTags.has(
                            tag
                        )
                    ) {
                        notification.close();

                        closed +=
                            1;
                    }
                }
            );


            return {
                ok:
                    true,

                closed
            };

        } catch (
            error
            ) {
            console.error(
                "clearPushNotificationsByTags error:",
                error
            );


            return {
                ok:
                    false,

                closed:
                    0,

                reason:
                    error?.message ||
                    "Не удалось закрыть push-уведомления"
            };
        }
    };


/*
 * Уведомления конкретного квартирного чата.
 *
 * Push V2 использует tag:
 * chat-${chatSessionId}
 */
export const clearChatPushNotifications =
    async (
        chatSessionId
    ) => {
        const normalizedChatSessionId =
            String(
                chatSessionId ??
                ""
            ).trim();


        if (
            !normalizedChatSessionId
        ) {
            return {
                ok:
                    false,

                closed:
                    0,

                reason:
                    "Не передан chatSessionId"
            };
        }


        return clearPushNotificationsByTags([
            `chat-${normalizedChatSessionId}`
        ]);
    };

export const clearPushNotifications =
    async () => {
        try {
            if (
                "serviceWorker" in
                navigator
            ) {
                const registration =
                    await navigator
                        .serviceWorker
                        .getRegistration();


                if (
                    registration &&
                    "getNotifications" in
                    registration
                ) {
                    const notifications =
                        await registration
                            .getNotifications();


                    notifications.forEach(
                        (
                            notification
                        ) => {
                            notification
                                .close();
                        }
                    );
                }
            }


            if (
                "clearAppBadge" in
                navigator
            ) {
                try {
                    await navigator
                        .clearAppBadge();

                } catch (
                    error
                    ) {
                    console.warn(
                        "clearAppBadge error:",
                        error
                    );
                }
            }


            return {
                ok:
                    true
            };

        } catch (
            error
            ) {
            console.error(
                "clearPushNotifications error:",
                error
            );


            return {
                ok:
                    false,

                reason:
                    error?.message ||
                    "Не удалось очистить уведомления"
            };
        }
    };

export const requestPushWorkerFlush = async () => {
    try {
        if (!("serviceWorker" in navigator)) return;
        // No active worker means nothing to flush yet. Do not wait indefinitely
        // for .ready; activation and subsequent foreground/poll calls retry.
        const registration = await navigator.serviceWorker.getRegistration();
        const worker = registration?.active || navigator.serviceWorker.controller;
        worker?.postMessage({ type: "ARENTO_PUSH_FLUSH" });
    } catch (error) {
        console.warn("Push worker flush request error:", error);
    }
};

const createPushActivationRequestId =
    () => {
        if (
            window.crypto &&
            typeof window.crypto.randomUUID ===
            "function"
        ) {
            return window.crypto
                .randomUUID();
        }


        if (
            !window.crypto ||
            typeof window.crypto.getRandomValues !==
            "function"
        ) {
            throw new Error(
                "Браузер не поддерживает безопасную генерацию requestId"
            );
        }


        const bytes =
            new Uint8Array(
                16
            );


        window.crypto
            .getRandomValues(
                bytes
            );


        bytes[6] =
            (
                bytes[6] &
                0x0f
            ) |
            0x40;

        bytes[8] =
            (
                bytes[8] &
                0x3f
            ) |
            0x80;


        const hex =
            Array.from(
                bytes,
                byte =>
                    byte
                        .toString(16)
                        .padStart(
                            2,
                            "0"
                        )
            );


        return (
            `${hex.slice(0, 4).join("")}-` +
            `${hex.slice(4, 6).join("")}-` +
            `${hex.slice(6, 8).join("")}-` +
            `${hex.slice(8, 10).join("")}-` +
            `${hex.slice(10, 16).join("")}`
        );
    };


const wait =
    (
        ms
    ) =>
        new Promise(
            resolve =>
                window.setTimeout(
                    resolve,
                    ms
                )
        );


export const startPushActivationTest =
    async (
        requestId,
        authIdentity = getAuthIdentity()
    ) => {
        const credential =
            await getPushDeviceCredential();


        if (!credential) {
            throw new Error(
                "Не найден credential текущего push-устройства"
            );
        }


        return apiRequest(
            "/api/push/device/activation-test/start",
            {
                method:
                    "POST",

                auth:
                    true,

                authIdentity,

                body: {
                    deviceId:
                        credential.deviceId,

                    deviceSecret:
                        credential.deviceSecret,

                    requestId
                }
            }
        );
    };


export const getPushActivationTestStatus =
    async ({
               requestId,
               deliveryId,
               authIdentity = getAuthIdentity()
           }) => {
        const credential =
            await getPushDeviceCredential();


        if (!credential) {
            throw new Error(
                "Не найден credential текущего push-устройства"
            );
        }


        return apiRequest(
            "/api/push/device/activation-test/status",
            {
                method:
                    "POST",

                auth:
                    true,

                authIdentity,

                body: {
                    deviceId:
                        credential.deviceId,

                    deviceSecret:
                        credential.deviceSecret,

                    requestId,
                    deliveryId
                }
            }
        );
    };


const getActivationFailureMessage =
    (
        result
    ) => {
        switch (
            result?.state
        ) {
        case "subscription-changed":
            return (
                "Push-подписка устройства изменилась. Повторите проверку."
            );

        case "ownership-changed":
            return (
                "Push-устройство сменило аккаунт. Повторите подключение."
            );

        case "expired":
            return (
                "Устройство не подтвердило получение тестового уведомления."
            );

        case "failed": {
            const errorCode = String(result?.errorCode || "PUSH_DELIVERY_FAILED").replace(/[^A-Z0-9_]/g, "").slice(0, 80);
            const httpStatus = Number(result?.httpStatus);
            const suffix = ` (${errorCode}${httpStatus >= 400 && httpStatus <= 599 ? `, HTTP ${httpStatus}` : ""})`;
            return "Тестовое уведомление не удалось доставить на это устройство." + suffix;
        }

        default:
            return (
                "Не удалось подтвердить работу уведомлений."
            );
        }
    };


const runPushActivationTest =
    async ({
               timeoutMs = 45 * 1000,
               pollIntervalMs = 900,
               enableGlobally = true,
               skipEnable = false
           } = {}) => {
        /*
         * 1. Реальное permission + subscription + authenticated claim.
         */
        const authIdentity = getAuthIdentity();
        const enabled = skipEnable ? { ok: true } : await enablePush({ enableGlobally });


        if (
            !enabled?.ok
        ) {
            return {
                ...enabled,

                verified:
                    false
            };
        }


        const credential =
            await getPushDeviceCredential();


        if (!credential) {
            return {
                ok:
                    false,

                verified:
                    false,

                reason:
                    "Arento не смог сохранить идентификатор этого устройства"
            };
        }


        let requestId;


        try {
            requestId =
                createPushActivationRequestId();
        } catch (
            error
            ) {
            return {
                ok:
                    false,

                verified:
                    false,

                reason:
                    getErrorReason(
                        error
                    )
            };
        }


        let started =
            null;

        let startError =
            null;


        /*
         * Один requestId делает повтор POST идемпотентным.
         * Если commit прошёл, а HTTP-ответ потерялся,
         * повтор не создаст второе тестовое уведомление.
         */
        for (
            let attempt = 1;
            attempt <= 3;
            attempt += 1
        ) {
            try {
                started =
                    await startPushActivationTest(
                        requestId,
                        authIdentity
                    );

                startError =
                    null;

                break;

            } catch (
                error
                ) {
                startError =
                    error;


                if (
                    error?.status >= 400 &&
                    error?.status < 500
                ) {
                    break;
                }


                if (
                    attempt <
                    3
                ) {
                    await wait(
                        350 *
                        attempt
                    );
                }
            }
        }


        if (
            !started?.deliveryId
        ) {
            return {
                ok:
                    false,

                verified:
                    false,

                reason:
                    startError?.message ||
                    "Не удалось запустить проверку уведомлений"
            };
        }


        const deadline =
            Date.now() +
            Math.max(
                5000,
                Number(
                    timeoutMs
                ) ||
                45000
            );


        let lastResult =
            null;

        let lastPollError =
            null;


        while (
            Date.now() <
            deadline
        ) {
            if (getAuthIdentity() !== authIdentity) {
                return { ok: false, verified: false, state: "ownership-changed", reason: "Аккаунт изменился. Повторите подключение уведомлений." };
            }

            /*
             * Если SW уже показал уведомление, но ACK остался
             * в durable IndexedDB, просим его отправить очередь.
             */
            await requestPushWorkerFlush();


            try {
                const result =
                    await getPushActivationTestStatus({
                        requestId,
                        authIdentity,

                        deliveryId:
                            started.deliveryId
                    });


                lastResult =
                    result;

                lastPollError =
                    null;


                if (
                    result?.verified ===
                    true
                ) {
                    /*
                     * activation-test/status уже проверил именно этот PushDevice,
                     * ownershipVersion и subscriptionVersion и получил реальный ACK.
                     * Повторная verification-status здесь не нужна: она создаёт
                     * ложную гонку сразу после успешно доставленного теста.
                     */
                    return {
                        ok:
                            true,

                        verified:
                            true,

                        requestId,

                        deliveryId:
                            started.deliveryId,

                        acknowledgedAt:
                            result.acknowledgedAt ||
                            null
                    };
                }


                if (
                    result?.terminal ===
                    true
                ) {
                    return {
                        ok:
                            false,

                        verified:
                            false,

                        requestId,

                        deliveryId:
                            started.deliveryId,

                        state:
                            result.state,

                        errorCode: result.errorCode || null,
                        httpStatus: result.httpStatus || null,
                        subscriptionRenewed: enabled.subscriptionRenewed === true,

                        reason:
                            getActivationFailureMessage(
                                result
                            )
                    };
                }

            } catch (
                error
                ) {
                lastPollError =
                    error;


                /*
                 * Identity/auth ошибки повторным poll не лечатся.
                 */
                if (
                    [
                        400,
                        401,
                        403,
                        404
                    ].includes(
                        Number(
                            error?.status
                        )
                    )
                ) {
                    return {
                        ok:
                            false,

                        verified:
                            false,

                        requestId,

                        deliveryId:
                            started.deliveryId,

                        reason:
                            error?.message ||
                            "Не удалось проверить текущее устройство"
                    };
                }
            }


            await wait(
                Math.max(
                    300,
                    Number(
                        pollIntervalMs
                    ) ||
                    900
                )
            );
        }


        return {
            ok:
                false,

            verified:
                false,

            requestId,

            deliveryId:
                started.deliveryId,

            state:
                lastResult?.state ||
                "timeout",

            reason:
                lastPollError?.message ||
                (
                    lastResult?.state ===
                    "accepted"
                        ? (
                            "Push-сервис принял уведомление, но Arento " +
                            "не получил подтверждение от этого устройства."
                        )
                        : (
                            "Не удалось подтвердить получение уведомления этим устройством."
                        )
                )
        };
    };

let activationInFlight = null;

export const enableAndVerifyPush = (options = {}) => {
    const authIdentity = getAuthIdentity();
    if (activationInFlight?.authIdentity === authIdentity) return activationInFlight.promise;

    const run = { authIdentity, promise: null };
    run.promise = (async () => {
        let result = await runPushActivationTest(options);
        const rejectedEndpoint = ["PUSH_PROVIDER_404", "PUSH_PROVIDER_410"].includes(result?.errorCode);
        if (rejectedEndpoint && !result.subscriptionRenewed && getAuthIdentity() === authIdentity) {
            // One repair only, and never re-enable an account disabled while testing.
            const restored = await enablePush({ renewSubscription: true, enableGlobally: false });
            if (!restored?.ok) return { ...result, reason: restored?.reason || result.reason };
            result = await runPushActivationTest({ ...options, enableGlobally: false, skipEnable: true });
        }
        return result;
    })().finally(() => {
        if (activationInFlight === run) activationInFlight = null;
    });
    activationInFlight = run;
    return run.promise;
};

export const getCurrentPushVerificationStatus = async () => {
    const authIdentity = getAuthIdentity();
    const inactive = { ok: true, verified: false, ready: false };
    if (!getToken() || !window.isSecureContext || !("serviceWorker" in navigator) ||
        !("Notification" in window) || Notification.permission !== "granted" ||
        (isIos() && !isStandalone())) return inactive;

    // An account ACK or an old device credential does not prove a live local subscription.
    const { subscription } = await getBrowserSubscription({ registerServiceWorker: false });
    if (!subscription || (subscription.expirationTime && subscription.expirationTime <= Date.now())) return inactive;
    const credential = await getPushDeviceCredential();
    if (!credential || getAuthIdentity() !== authIdentity) return inactive;

    const result = await apiRequest("/api/push/device/verification-status", {
        method: "POST", auth: true, authIdentity,
        body: { deviceId: credential.deviceId, deviceSecret: credential.deviceSecret }
    });
    if (!result?.ready) return result;
    if (!window.crypto?.subtle) return { ...result, verified: false, ready: false };
    const value = serializeSubscription(subscription);
    const signatureInput = [value.endpoint, value.keys?.p256dh, value.keys?.auth].map(item => String(item || "").trim()).join("\n");
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(signatureInput));
    const fingerprint = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    if (result.subscriptionFingerprint !== fingerprint || getAuthIdentity() !== authIdentity) {
        return { ...result, verified: false, ready: false, reason: "subscription-changed" };
    }
    return result;
};

export const getAccountPushVerificationStatus = async () => {
    if (!getToken()) return { ok: false, verified: false, reason: "not-authenticated" };
    return apiRequest("/api/push/device/account-verification-status", { auth: true });
};
